// Angular + External
import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";

import { ActiveUserProfilePromiseService } from "shared/cubiq-services";
import { BfxAuthService } from "./bfx-auth.service";
import { SystemMessage } from "shared/cubiq-model-multi-asset";
import { BfxDialogData } from "../../bfx-dialogs/models/bfx-dialog-data.model";
import { BfxDialogCommand } from "../../bfx-dialogs/models/bfx-dialog-command.enum";
import { TranslateService } from "@ngx-translate/core";

import {
  ConnectionStatusPromiseService,
  ConnectionStatusState,
  ConnectionStatus,
} from "shared/broker-client-services";
import { BfxConfigService } from "shared/auth-services/system-config/bfx-config.service";
import { BfxPrimeService } from "app/bfx-common/services/bfx-prime.service";
import { LogoutNotificationService } from "app/bfx-auth/logout-notification.service";
import { LoggerService } from "@bfx/logger";
import { UserAuthPromiseService } from "../../../shared/cubiq-services/auth/impl/UserAuthPromiseService";
const logger = LoggerService.for(__dirname);

@Injectable()
export class BfxReconnectDialogService {
  private systemMessages$: Observable<SystemMessage>;
  private reconnectDialogSubject = new Subject<BfxDialogData>();
  public reconnect$: Observable<BfxDialogData> = this.reconnectDialogSubject.asObservable();
  private hasConnected = false;
  private hasShownDialog = false;
  private reconnectDialogNoResponse = false;
  private autoReconnectionTimeout = 60000;
  private hasPrimed = false;
  private hasLogout = false;

  constructor(
    private _bfxAuthService: BfxAuthService,
    private _connectionStatusService: ConnectionStatusPromiseService,
    private activeUserService: ActiveUserProfilePromiseService,
    private translationService: TranslateService,
    protected _userAuthService: UserAuthPromiseService,
    private _configService: BfxConfigService,
    private _primeService: BfxPrimeService,
    private _logoutNotificationService: LogoutNotificationService,
  ) {
    this._primeService.waitPrimed().then((primed) => {
      this.hasPrimed = primed;
    });

    this._configService.getUIConfig().then((config) => {
      this.autoReconnectionTimeout = config.session.autoReconnectionTimeout;
    });

    this._logoutNotificationService.getLogoutNotification().subscribe(
      (v) => {
        this.hasLogout = true;
        // don't supress logout!
        v.res(true);
      },
      () => {},
      () => {},
    );

    this._connectionStatusService.getConnectionEvents().subscribe((event: ConnectionStatus) => {
      switch (event.getStatus()) {
        case ConnectionStatusState.CONNECTED:
          this.hasConnected = true;
          break;
        case ConnectionStatusState.RELOAD:
          if (!this.hasConnected || !this.hasPrimed || this.hasShownDialog || this.hasLogout) {
            break;
          }

          if (this._bfxAuthService.getSSOContextPath() == null) {
            this.promptReconnect();
          } else {
            // If it is an SSO login, just restart will log us back in
            this._bfxAuthService.restartLogout();
          }
          break;
        default:
          break;
      }
    });
  }

  public promptReconnect() {
    this.hasShownDialog = true;
    this.reconnectDialogNoResponse = false;

    this.reconnectDialogSubject.next(
      new BfxDialogData(BfxDialogCommand.DIALOG_RECONNECT_CONFIRM, null, null, {
        title: this.translationService.instant("reconnect.dialog.title"),
        messages: [this.translationService.instant("reconnect.dialog.message")],
        subMessage: this.translationService.instant("reconnect.dialog.submessage"),
        callback: () => {
          this._bfxAuthService.restartLogout();
        },
        callbackNegative: () => {
          this.hasShownDialog = false;
          this.reconnectDialogNoResponse = true;
        },
        autoReconnectionTimeout: this.autoReconnectionTimeout,
      }),
    );
  }

  public hasReconnectDialogNoResponse(): boolean {
    return this.reconnectDialogNoResponse;
  }
}
